import React, { useEffect, useMemo, useState } from 'react';
import { FiCopy, FiExternalLink, FiImage, FiRefreshCw, FiStar } from 'react-icons/fi';
import { JewelryService } from '../services/ZashaniService';
import { ButtonSpinner, MetricSkeleton, TableEmptyState } from '../components/LoadingStates';

const currency = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' });

const CatalogBuilder = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState('');
    const [message, setMessage] = useState('');

    const publicUrl = `${window.location.origin}/catalogo`;

    const stats = useMemo(() => {
        const published = items.filter((item) => item.catalogVisible);
        return {
            total: items.length,
            published: published.length,
            featured: published.filter((item) => item.catalogFeatured).length,
            withImage: published.filter((item) => item.image?.hasImage || item.image?.dataUrl).length,
        };
    }, [items]);

    const load = async () => {
        setLoading(true);
        setMessage('');
        try {
            const rows = await JewelryService.list();
            setItems(rows);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar el inventario.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateCatalogState = async (item, patch) => {
        setSavingId(item._id);
        setMessage('');
        try {
            const payload = {
                catalogVisible: patch.catalogVisible ?? item.catalogVisible,
                catalogFeatured: patch.catalogFeatured ?? item.catalogFeatured,
            };
            if (!payload.catalogVisible) {
                payload.catalogFeatured = false;
            }
            const updated = await JewelryService.update(item._id, payload);
            setItems((current) => current.map((row) => (row._id === item._id ? updated : row)));
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible actualizar el catalogo.');
        } finally {
            setSavingId('');
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setMessage('Enlace copiado.');
        } catch (error) {
            setMessage(publicUrl);
        }
    };

    return (
        <section className="content-section">
            <div className="section-heading">
                <div>
                    <p className="eyebrow">Catalogo virtual</p>
                    <h2>Creador de catalogo</h2>
                </div>
                <div className="report-actions">
                    <button className="ghost-button" type="button" onClick={copyLink}>
                        <FiCopy />
                        <span>Copiar enlace</span>
                    </button>
                    <a className="primary-button" href="/catalogo" target="_blank" rel="noreferrer">
                        <FiExternalLink />
                        <span>Ver publico</span>
                    </a>
                </div>
            </div>

            {message && <div className="notice">{message}</div>}

            {loading ? (
                <MetricSkeleton count={4} />
            ) : (
                <div className="metric-grid">
                    <article className="metric-card"><span>Productos</span><strong>{stats.total}</strong></article>
                    <article className="metric-card"><span>Publicados</span><strong>{stats.published}</strong></article>
                    <article className="metric-card"><span>Destacados</span><strong>{stats.featured}</strong></article>
                    <article className="metric-card"><span>Con imagen</span><strong>{stats.withImage}</strong></article>
                </div>
            )}

            <div className="panel catalog-builder-panel">
                <div className="table-toolbar">
                    <div>
                        <h2>Productos del catalogo</h2>
                        <span className="table-subtitle">{publicUrl}</span>
                    </div>
                    <button className={`icon-button ${loading ? 'is-spinning' : ''}`} type="button" onClick={load} disabled={loading} aria-label="Actualizar">
                        <FiRefreshCw />
                    </button>
                </div>
                <div className="table-wrap">
                    {loading || items.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="No hay productos para publicar." />
                    ) : (
                        <table>
                            <thead><tr><th>Producto</th><th>Categoria</th><th>Precio</th><th>Stock</th><th>Publico</th><th>Destacado</th></tr></thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item._id} className={item.catalogVisible ? '' : 'muted-row'}>
                                        <td>
                                            <div className="catalog-product-cell">
                                                <div className={`product-thumb ${item.image?.hasImage ? 'has-image' : ''}`}>
                                                    {item.image?.dataUrl ? <img src={item.image.dataUrl} alt={item.code} /> : <FiImage />}
                                                </div>
                                                <div>
                                                    <strong>{item.code}</strong>
                                                    <small>{item.description || 'Sin descripcion'}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{item.category || 'Sin categoria'}</td>
                                        <td>{currency.format(item.suggestedPrice || 0)}</td>
                                        <td>{item.stock}</td>
                                        <td>
                                            <label className="switch-row compact">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(item.catalogVisible)}
                                                    disabled={savingId === item._id || item.status !== 'active' || item.stock <= 0}
                                                    onChange={(event) => updateCatalogState(item, { catalogVisible: event.target.checked })}
                                                />
                                                <span>{savingId === item._id ? <ButtonSpinner /> : 'Publicado'}</span>
                                            </label>
                                        </td>
                                        <td>
                                            <button
                                                className={`icon-button ${item.catalogFeatured ? 'is-featured' : ''}`}
                                                type="button"
                                                disabled={savingId === item._id || !item.catalogVisible}
                                                onClick={() => updateCatalogState(item, { catalogFeatured: !item.catalogFeatured })}
                                                aria-label={item.catalogFeatured ? 'Quitar destacado' : 'Destacar producto'}
                                            >
                                                <FiStar />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </section>
    );
};

export default CatalogBuilder;
